/**
 * UUID v7 generado en el cliente.
 *
 * Esta función es la que hace barato todo el modelo offline. Como el ID lo
 * genera la tablet —no la base— una orden creada sin señal ya nace con su
 * identidad definitiva: no hay IDs temporales que reconciliar después, y
 * reenviar el mismo evento dos veces es inofensivo porque el backend
 * responde con la orden existente en vez de crear un duplicado.
 *
 * v7 y no v4 porque los primeros 48 bits son el timestamp en milisegundos:
 * los IDs quedan ordenados por tiempo de creación, lo que evita la
 * fragmentación de índices en Postgres cuando llegan miles de órdenes.
 *
 * Formato: unix_ts_ms (48b) | ver=7 (4b) | rand_a (12b) | var=2 (2b) | rand_b (62b)
 */
let ultimoMs = 0;
let contador = 0;

export function uuidv7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);

  let ms = Date.now();

  // Contador monótono en los 12 bits de `rand_a`. Sin esto, dos IDs generados
  // en el mismo milisegundo —agregar tres productos de un toque rápido— no
  // quedan ordenados entre sí, y se pierde justamente la propiedad por la que
  // se eligió v7 sobre v4.
  if (ms === ultimoMs) {
    contador = (contador + 1) & 0x0fff;
    // Desbordó el contador en un mismo milisegundo (4096 IDs). Imposible en un
    // POS, pero prestarle un milisegundo al futuro es más barato que romper
    // el orden.
    if (contador === 0) ms = ++ultimoMs;
  } else {
    ultimoMs = ms;
    // Arranca en la mitad baja del rango para dejar cabecera al contador.
    contador = (((bytes[6] ?? 0) << 8) | (bytes[7] ?? 0)) & 0x07ff;
  }
  bytes[0] = (ms / 2 ** 40) & 0xff;
  bytes[1] = (ms / 2 ** 32) & 0xff;
  bytes[2] = (ms / 2 ** 24) & 0xff;
  bytes[3] = (ms / 2 ** 16) & 0xff;
  bytes[4] = (ms / 2 ** 8) & 0xff;
  bytes[5] = ms & 0xff;

  // Versión 7 en el nibble alto del byte 6; el resto del byte 6 y todo el 7
  // llevan el contador monótono.
  bytes[6] = 0x70 | ((contador >> 8) & 0x0f);
  bytes[7] = contador & 0xff;
  // Variante RFC 4122 en los dos bits altos del byte 8.
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return (
    hex.slice(0, 8) +
    '-' +
    hex.slice(8, 12) +
    '-' +
    hex.slice(12, 16) +
    '-' +
    hex.slice(16, 20) +
    '-' +
    hex.slice(20)
  );
}

/** Identificador estable de esta instalación. Viaja en cada orden. */
const CLAVE_DISPOSITIVO = 'casanugget.dispositivoId';

export function dispositivoId(): string {
  let id = localStorage.getItem(CLAVE_DISPOSITIVO);
  if (!id) {
    id = uuidv7();
    localStorage.setItem(CLAVE_DISPOSITIVO, id);
  }
  return id;
}

export function nombreDispositivo(): string {
  return localStorage.getItem('dispositivoNombre') ?? 'Caja';
}